import CfSectionShell, { CfSectionShellFields } from '../cf-section-shell'
import type { Logo } from '../logostrip/CfLogoStripSection'
import { LinkCtaNext } from '~/components-v2'
import { contentfulImage } from '~/utils/contentful-image'

export interface TestimonialFields {
  alignQuote?: 'left' | 'right'
  cite: string
  ctaLabel?: string
  ctaUrl?: string
  headline?: string
  logo: Logo
  quote: string
}

interface CfTestimonialSectionProps {
  fields: CfSectionShellFields & TestimonialFields
}

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url)

export function CfTestimonialSection({ fields }: CfTestimonialSectionProps) {
  const { id, paddingTop, paddingBottom, showBottomBorder, theme } = fields
  const { headline, quote, cite, ctaUrl, ctaLabel, logo } = fields
  const logoUrl = logo?.fields?.logo?.fields?.file?.url
  const logoName = logo?.fields?.name
  const ctaIsExternal = ctaUrl ? isExternalUrl(ctaUrl) : false
  // Editors can override the CTA label per entry (e.g. 'Read blog post').
  const ctaText = ctaLabel?.trim() || 'Read case study'

  return (
    <CfSectionShell
      id={id}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      showBottomBorder={showBottomBorder}
      theme={theme}
    >
      <div className="cf-testimonial">
        {headline && (
          <hgroup className="cf-testimonial__header">
            <h2 className="heading-xl">{headline}</h2>
          </hgroup>
        )}

        <article className="cf-testimonial__slide">
          <div className="cf-testimonial__partner">
            {logoUrl ? (
              <img
                alt={logoName ?? ''}
                className="cf-testimonial__logo"
                src={contentfulImage(logoUrl, { w: 400 })}
              />
            ) : null}

            {ctaUrl ? (
              <LinkCtaNext
                className="cf-testimonial__cta cf-testimonial__cta--partner"
                external={ctaIsExternal}
                href={ctaIsExternal ? ctaUrl : undefined}
                to={ctaIsExternal ? undefined : ctaUrl}
              >
                {ctaText}
              </LinkCtaNext>
            ) : null}
          </div>

          <blockquote className="cf-testimonial__quote">
            <p className="heading-s">{quote}</p>
            <cite>{cite}</cite>
          </blockquote>

          {ctaUrl ? (
            <LinkCtaNext
              className="cf-testimonial__cta cf-testimonial__cta--mobile"
              external={ctaIsExternal}
              href={ctaIsExternal ? ctaUrl : undefined}
              to={ctaIsExternal ? undefined : ctaUrl}
            >
              {ctaText}
            </LinkCtaNext>
          ) : null}
        </article>
      </div>
    </CfSectionShell>
  )
}
